import React, { useState, useEffect, useRef } from 'react';
import { Input } from './ui/input';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './ui/command';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Check, Search, User, Building2, X } from 'lucide-react';
import { cn } from '../lib/utils';
import { clientsAPI } from '../services/api';

const ClientSearchInput = ({ 
  value, 
  onSelect, 
  placeholder = "Search clients...",
  disabled = false,
  className = "",
  typeFilter = null,
  showPhone = false,
  allowClear = false,
  "data-testid": dataTestId = "client-search"
}) => {
  const [open, setOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const inputRef = useRef(null);

  // Fetch clients on mount and when search query changes
  useEffect(() => {
    fetchClients();
  }, []);

  useEffect(() => {
    // Set initial selected client if value is provided
    if (value && clients.length > 0) {
      const client = clients.find(c => c._id === value);
      if (client) {
        setSelectedClient(client);
      }
    }
  }, [value, clients]);

  const fetchClients = async () => {
    try {
      const params = { limit: 500 };
      if (typeFilter) params.type = typeFilter;
      const response = await clientsAPI.getAll(params);
      const fetchedClients = response.data?.clients || [];
      setClients(fetchedClients);
    } catch (error) {
      console.error('ClientSearchInput - Error fetching clients:', error);
      try {
        const fallbackParams = { limit: 500 };
        if (typeFilter) fallbackParams.type = typeFilter;
        const fallbackResponse = await clientsAPI.getAll(fallbackParams);
        setClients(fallbackResponse.data?.clients || []);
      } catch (fallbackError) {
        console.error('ClientSearchInput - Fallback also failed:', fallbackError);
        setClients([]);
      }
    }
  };

  const filteredClients = clients.filter(client => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      client.name?.toLowerCase().includes(query) ||
      client.email?.toLowerCase().includes(query) ||
      client.contactPerson?.toLowerCase().includes(query) ||
      client.clientCode?.toLowerCase().includes(query) ||
      client.phone?.toLowerCase().includes(query)
    );
  });

  const handleSelect = (client) => {
    setSelectedClient(client);
    setSearchQuery('');
    setOpen(false);
    onSelect(client._id, client.name, client);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setSelectedClient(null);
    setSearchQuery('');
    onSelect('', '', null);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div 
          className={cn(
            "flex items-center gap-2 w-full border rounded-md px-3 py-2 cursor-pointer hover:bg-gray-50",
            disabled && "opacity-50 cursor-not-allowed",
            className
          )}
          onClick={() => !disabled && setOpen(true)}
          data-testid={dataTestId}
        >
          <Search className="h-4 w-4 text-gray-400" />
          <span className={cn(
            "flex-1 text-left",
            !selectedClient && "text-gray-400"
          )}>
            {selectedClient ? selectedClient.name : placeholder}
          </span>
          {selectedClient && allowClear && (
            <button onClick={handleClear} className="text-gray-400 hover:text-gray-600">
              <X className="h-4 w-4" />
            </button>
          )}
          {selectedClient && !allowClear && (
            <span className="text-xs text-gray-400">
              {selectedClient.type === 'Corporation' ? <Building2 className="h-3 w-3" /> : <User className="h-3 w-3" />}
            </span>
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput 
            placeholder="Type to search clients..." 
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          <CommandList>
            <CommandEmpty>No clients found.</CommandEmpty>
            <CommandGroup>
              {filteredClients.slice(0, 50).map((client) => (
                <CommandItem
                  key={client._id}
                  value={client._id}
                  onSelect={() => handleSelect(client)}
                  className="cursor-pointer"
                >
                  <div className="flex items-center gap-3 w-full">
                    <div className={cn(
                      "h-8 w-8 rounded-full flex items-center justify-center text-sm font-medium",
                      client.type === 'Corporation' ? "bg-purple-100 text-purple-600" : "bg-blue-100 text-blue-600"
                    )}>
                      {client.name?.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium truncate">{client.name}</span>
                        {client.clientCode && (
                          <span className="text-xs font-mono bg-gray-100 px-1.5 py-0.5 rounded text-gray-600">
                            {client.clientCode}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <span className="truncate">{client.email}</span>
                        {showPhone && client.phone && (
                          <span className="text-gray-400">{client.phone}</span>
                        )}
                      </div>
                    </div>
                    <span className="text-xs text-gray-400 capitalize">{client.type}</span>
                    {value === client._id && (
                      <Check className="h-4 w-4 text-green-500" />
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default ClientSearchInput;
